import { v4 as uuid } from 'uuid';

import { createManagerFactory, formBuilder, ValidationTrigger } from 'app-components/utils/createForm/formBuilder';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { BUSINESS_REDUCER_KEY } from '../business';
import { getDefaultBusiness } from '../business/businessSelectors';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors } from '../categories';
import { chartOfAccountsTypes, chartOfAccountsUtils } from '../chart-of-accounts';
import { type ENTITLEMENTS_REDUCER_KEY, entitlementsSelectors } from '../entitlements';
import { findEquivalentCategory, getUseDefaultAccountInitialValue } from './transactionRulesSelectors';
import {
  type AccountSourceConditionType,
  type AccountType,
  type AmountSourceMatchingType,
  type AmountType,
  type CategoryTargetConditionsType,
  ConditionsEnum,
  defaultAmountSourceCondition,
  defaultPayeeSourceCondition,
  type PayeeMatchingOperatorType,
  type PayeeMatchingType,
  type PayeeNameType,
  type PayeeSourceConditionType,
  type SubmitPayload,
  type TRANSACTION_RULES_REDUCER_KEY,
  type TransactionRuleConditionStatusType,
  type TransactionRuleConditionStatusTypeKeys,
  type TransactionRuleConditionUnion,
  type TransactionRuleFormInput,
  type TransactionRuleType,
} from './transactionRulesTypes';
import { convertAmountByType } from './transactionRulesUtils';

type ReduxState = Pick<
  RootState,
  | typeof TRANSACTION_RULES_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof BUSINESS_REDUCER_KEY
  | typeof ENTITLEMENTS_REDUCER_KEY
>;

export const clearState: TransactionRuleType = {
  id: undefined,
  payeeSource: defaultPayeeSourceCondition,
  amountSource: defaultAmountSourceCondition,
  coaSource: undefined,
  accountSource: undefined,
  usageSource: undefined,
  payeeTarget: undefined,
  coaTarget: undefined,
  useDefaultAccountUsage: undefined,
  businessCoaTarget: undefined,
  tagsTarget: undefined,
  memoTarget: undefined,
  excludedTarget: undefined,
  reviewedTarget: undefined,
  applyToExistingTransactions: false,
  businessTargetId: undefined,
  // customerTargetId: undefined,
  // projectTargetId: undefined,
  // prevTransactionRuleId: undefined,
  // nextTransactionRuleId: undefined,
};

export type FormState = SubmitPayload & {
  initialValues: Partial<TransactionRuleType>;
  isBusinessEnabled: boolean;
};

const EMPTY_TAGS_ARRAY: string[] = [];

const syncBusinessCoaTargetEnabled = (draft: FormState): void => {
  draft.enabled.businessCoaTarget = Boolean(
    draft.enabled.coaTarget && draft.transactionRule.useDefaultAccountUsage && draft.transactionRule.businessCoaTarget,
  );
};

const init = ({ input, store }: { input: TransactionRuleFormInput; store: ReduxState }): FormState => {
  const { enabledOverrides, ...initialValues } = input;
  const useDefaultAccountUsage = getUseDefaultAccountInitialValue(store, input);
  const isBusinessEnabled = entitlementsSelectors.getBusinessEnabled(store);
  const hasLegacyAnyAmountSource = input.amountSource?.amountMatchingOperator === 'ANY_AMOUNT';

  if (hasLegacyAnyAmountSource) {
    delete initialValues.amountSource;
  }

  // Normalize amountSource amounts to always be positive (sign is determined by amountType)
  const normalizedAmountSource =
    input.amountSource && !hasLegacyAnyAmountSource
      ? {
          ...input.amountSource,
          amount1: input.amountSource.amount1 !== undefined ? Math.abs(input.amountSource.amount1) : undefined,
          // Only include amount2 if it exists on the input.amountSource (i.e., for "BETWEEN" operator)
          ...(input.amountSource.amountMatchingOperator === 'BETWEEN' && 'amount2' in input.amountSource
            ? {
                amount2:
                  input.amountSource.amount2 !== undefined
                    ? Math.abs((input.amountSource as { amount2?: number }).amount2!)
                    : undefined,
              }
            : {}),
        }
      : undefined;

  const transactionRule: TransactionRuleType = {
    id: input.id ?? undefined,
    payeeSource: input.payeeSource ?? undefined,
    ...(hasLegacyAnyAmountSource ? {} : { amountSource: normalizedAmountSource }),
    coaSource: input.coaSource,
    accountSource: input.accountSource ?? undefined,
    usageSource: input.usageSource,
    payeeTarget: input.payeeTarget,
    coaTarget: input.coaTarget,
    useDefaultAccountUsage,
    businessCoaTarget: input.businessCoaTarget,
    tagsTarget: input.tagsTarget,
    memoTarget: input.memoTarget,
    excludedTarget: input.excludedTarget,
    reviewedTarget: input.reviewedTarget,
    applyToExistingTransactions: input.applyToExistingTransactions,
    businessTargetId: input.businessTargetId,
  };

  // if input.id is undefined, then this is a new rule, so we need to set some default values
  // if input.id is defined, then this is an existing rule, so we need to use the values from the input
  const hasPayeeSourceValue = input.payeeSource?.payeeMatchingCriteria?.some(
    (c) => c.payeeName && c.payeeName.length > 0,
  );
  const hasAmountSourceValue = normalizedAmountSource?.amount1 !== undefined;

  const hasId = !!input.id;

  const valueDerivedEnabled: TransactionRuleConditionStatusType = {
    // Editing: only enable conditions that actually exist on the rule.
    // Creating: preserve defaults (some conditions have default objects even when "off").
    payeeSource: hasId ? !!input.payeeSource : hasPayeeSourceValue === true,
    amountSource: hasId ? !!input.amountSource : hasAmountSourceValue,
    coaSource: (input.coaSource?.length ?? 0) > 0,
    accountSource: !!input.accountSource,
    usageSource: !!input.usageSource,
    payeeTarget: (input.payeeTarget?.length ?? 0) > 0,
    coaTarget: hasId ? input.coaTarget !== undefined : !!input.coaTarget,
    businessCoaTarget: hasId ? input.businessCoaTarget !== undefined : !!input.businessCoaTarget,
    tagsTarget: (input.tagsTarget?.length ?? 0) > 0,
    memoTarget: (input.memoTarget?.length ?? 0) > 0,
    excludedTarget: input.excludedTarget !== undefined,
    reviewedTarget: input.reviewedTarget !== undefined && input.reviewedTarget !== null,
  };
  const enabled: TransactionRuleConditionStatusType = {
    ...valueDerivedEnabled,
    ...enabledOverrides,
  };
  if (hasLegacyAnyAmountSource) {
    enabled.amountSource = false;
  }
  if (enabledOverrides?.coaTarget !== undefined) {
    enabled.businessCoaTarget = Boolean(
      enabledOverrides.coaTarget && useDefaultAccountUsage && transactionRule.businessCoaTarget,
    );
  }

  return {
    transactionRule,
    enabled,
    initialValues,
    isBusinessEnabled,
  };
};

export const selectInitialValues = ({ state }: { state: FormState }) => state.initialValues;
export const selectInitialPayeeSourceCondition = (args: { state: FormState }) => selectInitialValues(args).payeeSource;

const isValidNumber = (val: string | number | null | undefined) =>
  val !== null && val !== '' && Number.isFinite(Number(val));

type CoaTargetCondition = ConditionsEnum.COA_TARGET_CONDITION | ConditionsEnum.BUSINESS_COA_TARGET_CONDITION;

const syncPairedCoaTarget = (draft: FormState, condition: CoaTargetCondition, store: ReduxState): void => {
  const selectedTarget =
    condition === ConditionsEnum.BUSINESS_COA_TARGET_CONDITION
      ? draft.transactionRule.businessCoaTarget
      : draft.transactionRule.coaTarget;
  if (!selectedTarget) return;

  const equivalent = findEquivalentCategory(store, selectedTarget.id);
  if (!equivalent?.id) return;

  const pairedTarget: CategoryTargetConditionsType = {
    id: equivalent.id,
    type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
  };
  if (condition === ConditionsEnum.BUSINESS_COA_TARGET_CONDITION) {
    draft.transactionRule.coaTarget = pairedTarget;
  } else {
    draft.transactionRule.businessCoaTarget = pairedTarget;
  }
};

export const initSourceCondition = (key: AccountType): AccountSourceConditionType => {
  switch (key) {
    case 'SPECIFIC':
      return { accountSourceType: 'SPECIFIC', accountSourceIds: [] };
    case 'BUSINESS':
      return { accountSourceType: 'BUSINESS' };
    case 'PERSONAL':
      return { accountSourceType: 'PERSONAL' };
    default:
      return { accountSourceType: 'SPECIFIC', accountSourceIds: [] };
  }
};

const transactionRulesForm = formBuilder({ init })
  // Enabled flags - start
  .addField('isEnabledPayeeSource', {
    selector: (state) => state.enabled.payeeSource,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.payeeSource = value;
    },
  })
  .addField('isEnabledAmountSource', {
    selector: (state) => state.enabled.amountSource,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.amountSource = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.amountSource ||
            (state.enabled.amountSource &&
              (state.enabled.payeeSource || state.enabled.coaSource || state.enabled.accountSource))
          );
        },
        error: 'Required',
      },
    },
  })
  .addField('isEnabledCoaSource', {
    selector: (state) => state.enabled.coaSource,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.coaSource = value;
    },
  })
  .addField('isEnabledAccountSource', {
    selector: (state) => state.enabled.accountSource,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.accountSource = value;
    },
  })
  // .addField('isEnabledUsageSourceType', {
  //   selector: (state) => state.enabled.usageSourceType,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.usageSourceType = value;
  //   },
  // })
  .addField('isEnabledPayeeTarget', {
    selector: (state) => state.enabled.payeeTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.payeeTarget = value;
    },
  })
  .addField('isEnabledCoaTarget', {
    selector: (state) => state.enabled.coaTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.coaTarget = value;
      syncBusinessCoaTargetEnabled(draft);
    },
  })
  .addField('isEnabledTagsTarget', {
    selector: (state) => state.enabled.tagsTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.tagsTarget = value;
    },
  })
  .addField('isEnabledMemoTarget', {
    selector: (state) => state.enabled.memoTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.memoTarget = value;
    },
  })
  .addField('isEnabledExcludedTarget', {
    selector: (state) => state.enabled.excludedTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.excludedTarget = value;
    },
  })
  .addField('isEnabledReviewedTarget', {
    selector: (state) => state.enabled.reviewedTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.reviewedTarget = value;
      value && (draft.transactionRule.reviewedTarget = false); // default to false if enabled
    },
  })
  // Enabled flags - end

  // Payee Source - start
  .addField('payeeSource', {
    selector: (state) => state.transactionRule.payeeSource || defaultPayeeSourceCondition,
    onValueChange: (draft, value: PayeeSourceConditionType) => {
      if (draft.transactionRule.payeeSource) {
        draft.transactionRule.payeeSource = value;
      }
    },
  })
  .addField('payeeSourceType', {
    selector: (state) => state.transactionRule.payeeSource?.payeeType || 'STATEMENT',
    onValueChange: (draft, value: PayeeNameType) => {
      if (draft.transactionRule.payeeSource?.payeeType) {
        draft.transactionRule.payeeSource.payeeType = value;
      }
    },
  })
  .addMultiField('payeeSourceMatchingOperator', {
    selectActiveKeys(state) {
      const indexes: number[] = [];
      if (state.transactionRule.payeeSource?.payeeMatchingCriteria) {
        for (let i = 0; i < state.transactionRule.payeeSource?.payeeMatchingCriteria.length; i++) {
          indexes.push(i);
        }
      }
      return indexes;
    },
    onValueChange: (draft, key, value: PayeeMatchingOperatorType) => {
      if (draft.transactionRule.payeeSource) {
        draft.transactionRule.payeeSource.payeeMatchingCriteria[key].payeeMatchingOperator = value;
      }
    },
    selector: (state, key) =>
      state.transactionRule.payeeSource?.payeeMatchingCriteria[key].payeeMatchingOperator || 'CONTAINS',
  })
  .addMultiField('payeeSourceName', {
    selectActiveKeys(state) {
      const indexes: number[] = [];
      if (state.transactionRule.payeeSource?.payeeMatchingCriteria) {
        for (let i = 0; i < state.transactionRule.payeeSource?.payeeMatchingCriteria.length; i++) {
          indexes.push(i);
        }
      }
      return indexes;
    },
    onValueChange: (draft, key, value: string) => {
      if (value.trim().length === 0 && key != 0) {
        // if any condition after the first one is blank splice it out of the array, otherwise we have blank conditions
        draft.transactionRule.payeeSource?.payeeMatchingCriteria.splice(key, 1);
      } else if (draft.transactionRule.payeeSource) {
        draft.transactionRule.payeeSource.payeeMatchingCriteria[key].payeeName = value;
      }
    },
    selector: (state, key) => state.transactionRule.payeeSource?.payeeMatchingCriteria[key].payeeName || '',
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, key) => {
          const valid =
            !state.enabled.payeeSource || !!state.transactionRule.payeeSource?.payeeMatchingCriteria[key].payeeName;
          return valid;
        },
        error: 'Required',
      },
      length: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, key) => {
          const valid =
            !state.enabled.payeeSource ||
            (state.transactionRule.payeeSource?.payeeMatchingCriteria[key].payeeName?.length ?? 0) >= 3;
          return valid;
        },
        error: 'Please enter payee pattern that is at least 3 characters',
      },
    },
  })
  .addAction('addPayeeSourceMatchingCriteria', (draft, value: PayeeMatchingType) => {
    draft.transactionRule.payeeSource?.payeeMatchingCriteria.push(value);
  })
  .addAction('removePayeeSourceMatchingCriteria', (draft, index: number) => {
    draft.transactionRule.payeeSource?.payeeMatchingCriteria.splice(index, 1);
  })
  // Payee Source - end

  // Account Source - start
  .addField('accountSourceType', {
    selector: (state) =>
      state.transactionRule.accountSource?.accountSourceType || (state.isBusinessEnabled ? 'BUSINESS' : 'SPECIFIC'),
    onValueChange: (draft, value: AccountType) => {
      draft.transactionRule.accountSource = initSourceCondition(value);
    },
  })
  .addMultiField('accountSourceIds', {
    selectActiveKeys(state) {
      const indexes: number[] = [];
      const source = state.transactionRule.accountSource;

      // if enabled but no source, then we need to have at least one index to show the field
      if (!source || (source.accountSourceType === 'SPECIFIC' && source.accountSourceIds.length <= 1)) {
        indexes[0] = 0; // always have at least one index if enabled & SPECIFIC
      } else if (source.accountSourceType === 'SPECIFIC') {
        for (let i = 1; i < source.accountSourceIds.length; i++) {
          indexes.push(i);
        }
      }
      return indexes;
    },
    selector: (state, key) =>
      state.transactionRule.accountSource && state.transactionRule.accountSource.accountSourceType === 'SPECIFIC'
        ? state.transactionRule.accountSource.accountSourceIds[key]
        : undefined,
    onValueChange: (draft, key, value: string | undefined) => {
      if (!draft.transactionRule.accountSource) {
        draft.transactionRule.accountSource = initSourceCondition(draft.isBusinessEnabled ? 'BUSINESS' : 'SPECIFIC');
      }
      if (draft.transactionRule.accountSource.accountSourceType === 'SPECIFIC') {
        draft.transactionRule.accountSource.accountSourceIds[key] = value;
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, key) => {
          const valid =
            !state.enabled.accountSource ||
            state.transactionRule.accountSource?.accountSourceType === 'BUSINESS' ||
            state.transactionRule.accountSource?.accountSourceType === 'PERSONAL' ||
            (state.transactionRule.accountSource?.accountSourceType === 'SPECIFIC' &&
              !!state.transactionRule.accountSource.accountSourceIds?.[key]);
          return valid;
        },
        error: 'Required',
      },
      length: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, key) => {
          const valid =
            !state.enabled.accountSource ||
            (state.transactionRule.accountSource?.accountSourceType === 'SPECIFIC' &&
              (state.transactionRule.accountSource.accountSourceIds?.[key]?.length ?? 0) >= 3);
          return valid;
        },
        error: 'Please enter account name that is at least 3 characters',
      },
    },
  })
  .addAction('addAccount', (draft) => {
    draft.transactionRule.accountSource
      ? draft.transactionRule.accountSource.accountSourceType === 'SPECIFIC' &&
        draft.transactionRule.accountSource.accountSourceIds?.push('')
      : (draft.transactionRule.accountSource = initSourceCondition('SPECIFIC'));
  })
  .addAction('removeAccount', (draft, index: number) => {
    draft.transactionRule.accountSource
      ? draft.transactionRule.accountSource.accountSourceType === 'SPECIFIC' &&
        draft.transactionRule.accountSource.accountSourceIds?.splice(index, 1)
      : (draft.transactionRule.accountSource = initSourceCondition('SPECIFIC'));
  })
  .addAction('setAccountType', (draft, value: AccountType) => {
    draft.transactionRule.accountSource = initSourceCondition(value);
  })
  // Account Source - end

  // Amount Source - start
  .addField('amountSourceType', {
    selector: (state) => state.transactionRule.amountSource?.amountType,
    onValueChange: (draft, value: AmountType) => {
      if (!draft.transactionRule.amountSource) {
        draft.transactionRule.amountSource = { ...defaultAmountSourceCondition };
      }
      draft.transactionRule.amountSource.amountType = value;
    },
  })
  .addField('amountSourceMatchingOperator', {
    selector: (state) =>
      state.transactionRule.amountSource?.amountMatchingOperator ?? defaultAmountSourceCondition.amountMatchingOperator,
    onValueChange: (draft, value: AmountSourceMatchingType) => {
      if (!draft.transactionRule.amountSource) {
        draft.transactionRule.amountSource = { ...defaultAmountSourceCondition };
      }
      draft.transactionRule.amountSource.amountMatchingOperator = value;
    },
  })
  .addField('amountSourceAmount1', {
    selector: (state) => {
      const amount = state.transactionRule.amountSource?.amount1;
      return amount !== undefined ? Math.abs(amount) : amount;
    },
    onValueChange: (draft, value?: number) => {
      const amount = Number(value);
      if (!draft.transactionRule.amountSource) {
        draft.transactionRule.amountSource = { ...defaultAmountSourceCondition };
      }
      if (!isNaN(amount)) {
        draft.transactionRule.amountSource.amount1 = Math.abs(amount);
      } else {
        draft.transactionRule.amountSource.amount1 = 0;
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          const valid = !state.enabled.amountSource || isValidNumber(state.transactionRule.amountSource?.amount1);
          return valid;
        },
        error: 'Required',
      },
    },
  })
  .addField('amountSourceAmount2', {
    selector: (state) => {
      if (state.transactionRule.amountSource?.amountMatchingOperator === 'BETWEEN') {
        const amount = state.transactionRule.amountSource?.amount2;
        return amount !== undefined ? Math.abs(amount) : amount;
      }
      return undefined;
    },
    onValueChange: (draft, value?: number) => {
      const amount = Number(value);
      if (draft.transactionRule.amountSource?.amountMatchingOperator === 'BETWEEN' && !isNaN(amount)) {
        draft.transactionRule.amountSource.amount2 = Math.abs(amount);
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          const isValid =
            !state.enabled.amountSource ||
            state.transactionRule.amountSource?.amountMatchingOperator !== 'BETWEEN' ||
            isValidNumber(state.transactionRule.amountSource?.amount2);
          return isValid;
        },
        error: 'Required',
      },
    },
  })
  // Amount Source - end

  // payload.value is the actual property value
  .addAction('setCondition', (draft, payload: TransactionRuleConditionUnion, store: ReduxState) => {
    const condition = payload?.condition;
    const value = payload?.value;

    if (!payload || condition === undefined) {
      return;
    } else {
      if (value === undefined) {
        draft.enabled[condition] = false;
        if (condition === ConditionsEnum.COA_TARGET_CONDITION) {
          syncBusinessCoaTargetEnabled(draft);
        }
        return;
      } else {
        if (condition in draft.transactionRule) {
          // @ts-expect-error TS2322
          draft.transactionRule[condition] = value as (typeof draft.transactionRule)[typeof condition];
          draft.enabled[condition] = true;
        }

        if (
          condition === ConditionsEnum.COA_TARGET_CONDITION ||
          condition === ConditionsEnum.BUSINESS_COA_TARGET_CONDITION
        ) {
          if (condition === ConditionsEnum.BUSINESS_COA_TARGET_CONDITION) {
            draft.enabled.coaTarget = true;
          }
          syncPairedCoaTarget(draft, condition, store);
          syncBusinessCoaTargetEnabled(draft);
        }
      }
    }
  })

  // Payee Target
  .addField('payeeTarget', {
    selector: (state) => state.transactionRule.payeeTarget || '',
    onValueChange: (draft, value: string) => {
      draft.transactionRule.payeeTarget = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.payeeTarget ||
            (!!state.transactionRule.payeeTarget && state.transactionRule.payeeTarget.trim() !== '')
          );
        },
        error: 'Required',
      },
    },
  })
  // Payee Target - end

  // COA Target - start
  .addField('coaTarget', {
    selector: (state) => state.transactionRule.coaTarget,
    onValueChange: (draft, value: CategoryTargetConditionsType | undefined, store) => {
      draft.transactionRule.coaTarget = value;
      // set exclude values based on category - the user can override these values explictly when we open that capability in the rule.
      const excludeValue = !!chartOfAccountsUtils.isTransferCOA(value);
      draft.transactionRule.excludedTarget = { isExcludedFromReports: excludeValue, isExcludedFromF2S: excludeValue };

      const isBizCat =
        value?.type === 'CATEGORY'
          ? categoriesSelectors.getCategoryById(store, value.id)?.usageType === 'BUSINESS'
          : undefined;

      // Set biz id if it is a biz category, user can explicitly override.
      if (!draft.transactionRule.businessTargetId && isBizCat) {
        const bizId = getDefaultBusiness(store)?.id;
        draft.transactionRule.businessTargetId = bizId;
      } else if (!isBizCat) {
        draft.transactionRule.businessTargetId = undefined;
      }

      if (
        draft.isBusinessEnabled &&
        draft.transactionRule.useDefaultAccountUsage &&
        !draft.transactionRule.businessCoaTarget &&
        draft.transactionRule.coaTarget
      ) {
        const equivalent = findEquivalentCategory(store, draft.transactionRule.coaTarget.id);
        if (equivalent?.id) {
          draft.transactionRule.businessCoaTarget = {
            id: equivalent.id,
            type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
          };
        }
      }
      syncBusinessCoaTargetEnabled(draft);
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return !state.enabled.coaTarget || !!state.transactionRule.coaTarget;
        },
        error: 'Required',
      },
    },
  })
  .addField('businessCoaTarget', {
    selector: (state) => state.transactionRule.businessCoaTarget,
    onValueChange: (draft, value: CategoryTargetConditionsType | undefined, store) => {
      draft.transactionRule.businessCoaTarget = value;
      draft.enabled.businessCoaTarget = !!value;
      // set exclude values based on category - the user can override these values explictly when we open that capability in the rule.
      const excludeValue = !!chartOfAccountsUtils.isTransferCOA(value);
      draft.transactionRule.excludedTarget = { isExcludedFromReports: excludeValue, isExcludedFromF2S: excludeValue };

      const isBizCat =
        value?.type === 'CATEGORY'
          ? categoriesSelectors.getCategoryById(store, value.id)?.usageType === 'BUSINESS'
          : undefined;

      // Set biz id if it is a biz category, user can explicitly override.
      if (!draft.transactionRule.businessTargetId && isBizCat) {
        const bizId = getDefaultBusiness(store)?.id;
        draft.transactionRule.businessTargetId = bizId;
      } else if (!isBizCat) {
        draft.transactionRule.businessTargetId = undefined;
      }

      if (
        draft.transactionRule.useDefaultAccountUsage &&
        !draft.transactionRule.coaTarget &&
        draft.transactionRule.businessCoaTarget
      ) {
        const equivalent = findEquivalentCategory(store, draft.transactionRule.businessCoaTarget.id);
        if (equivalent?.id) {
          draft.transactionRule.coaTarget = {
            id: equivalent.id,
            type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
          };
        }
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.coaTarget ||
            !state.transactionRule.useDefaultAccountUsage ||
            !!state.transactionRule.businessCoaTarget
          );
        },
        error: 'Required',
      },
    },
  })
  .addField('useDefaultAccountUsage', {
    selector: (state) => state.transactionRule.useDefaultAccountUsage || false,
    onValueChange: (draft, value: boolean) => {
      draft.transactionRule.useDefaultAccountUsage = value;
      draft.transactionRule.coaTarget = undefined;
      draft.transactionRule.businessCoaTarget = undefined;
      draft.transactionRule.businessTargetId = undefined;
      syncBusinessCoaTargetEnabled(draft);
    },
  })
  // COA Target - end

  // Tags Target - start
  .addField('tagsTarget', {
    selector: (state) => state.transactionRule.tagsTarget || EMPTY_TAGS_ARRAY,
    onValueChange: (draft, value: string[]) => {
      draft.transactionRule.tagsTarget = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.tagsTarget ||
            (!!state.transactionRule.tagsTarget && state.transactionRule.tagsTarget.length !== 0)
          );
        },
        error: 'Required',
      },
    },
  })
  // Tags Target - end

  // Memo Target - start
  .addField('memoTarget', {
    selector: (state) => state.transactionRule.memoTarget,
    onValueChange: (draft, value?: string) => {
      draft.transactionRule.memoTarget = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.memoTarget ||
            (!!state.transactionRule.memoTarget && state.transactionRule.memoTarget.length !== 0)
          );
        },
        error: 'Required',
      },
    },
  })
  // Memo Target - end

  // Excludes Target - start
  .addField('excludeFromReportsTarget', {
    selector: (state) => !!state.transactionRule.excludedTarget?.isExcludedFromReports,
    onValueChange: (draft, value: boolean) => {
      if (draft.transactionRule.excludedTarget) {
        draft.transactionRule.excludedTarget.isExcludedFromReports = value;
      } else {
        draft.transactionRule.excludedTarget = {
          isExcludedFromReports: value,
          isExcludedFromF2S: false,
        };
      }
    },
  })
  .addField('excludeFromF2STarget', {
    selector: (state) => !!state.transactionRule.excludedTarget?.isExcludedFromF2S,
    onValueChange: (draft, value: boolean) => {
      if (draft.transactionRule.excludedTarget) {
        draft.transactionRule.excludedTarget.isExcludedFromF2S = value;
      } else {
        draft.transactionRule.excludedTarget = {
          isExcludedFromReports: false,
          isExcludedFromF2S: value,
        };
      }
      draft.transactionRule.excludedTarget.isExcludedFromF2S = value;
    },
  })
  // Excludes Target - end

  // Reviewed Target - start
  .addField('reviewedTarget', {
    selector: (state) => !!state.transactionRule.reviewedTarget,
    onValueChange: (draft, value: boolean) => {
      draft.transactionRule.reviewedTarget = value;
    },
  })
  // Reviewed Target - end

  // bizId Target - start
  .addField('businessTargetId', {
    selector: (state) => state.transactionRule.businessTargetId,
    onValueChange: (draft, value: string | undefined) => {
      // If we switch from biz -> personal or personal -> biz. Set cat to undefined
      if ((!value && draft.transactionRule.businessTargetId) || (!draft.transactionRule.businessTargetId && value)) {
        draft.transactionRule.coaTarget = { type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED, id: '0' };
      }
      draft.transactionRule.businessTargetId = value;
    },
  })
  // bizId Target - end

  // Apply to Existing - start
  .addField('applyToExistingTransactions', {
    selector: (state) => state.transactionRule.applyToExistingTransactions || false,
    onValueChange: (draft, value: boolean) => {
      draft.transactionRule.applyToExistingTransactions = value;
    },
  })
  // Apply to Existing - end

  .build({
    submitTransform(state): SubmitPayload {
      // at this point, remove all enabled === false properties from the transactionRule
      const pruned = { ...state.transactionRule };
      const keys = Object.keys(state.enabled) as TransactionRuleConditionStatusTypeKeys[];
      const filteredProps = keys.filter(
        (key: keyof TransactionRuleConditionStatusType) => state.enabled[key] === false,
      );
      filteredProps.forEach((key: keyof TransactionRuleConditionStatusType) => {
        // delete props that are disabled OR the value is undefined.
        if (state.enabled[key] === false || state.transactionRule[key] === undefined) {
          delete pruned[key];
        }
      });
      // Handle special mappings for exclude fields
      if (!state.enabled.excludedTarget) {
        delete pruned.excludedTarget;
      }

      if (!state.enabled.coaTarget && !state.enabled.businessCoaTarget) {
        delete pruned.coaTarget;
        delete pruned.businessCoaTarget;
        delete pruned.businessTargetId;
        delete pruned.useDefaultAccountUsage;
      }

      // Handle amountSource conversion
      if (pruned.amountSource && state.enabled.amountSource) {
        const { amountType, amount1, amountMatchingOperator } = pruned.amountSource;
        pruned.amountSource = {
          ...pruned.amountSource,
          amount1: convertAmountByType(amount1, amountType),
        };
        if (amountMatchingOperator === 'BETWEEN' && pruned.amountSource.amount2 !== undefined) {
          pruned.amountSource.amount2 = convertAmountByType(pruned.amountSource.amount2, amountType);
        }
      }

      const value = {
        ...state,
        transactionRule: {
          ...pruned,
          ...(state.transactionRule.id ? { id: state.transactionRule.id } : { clientId: uuid().toUpperCase() }),
        },
      };

      return value;
    },
  });

export const transactionRulesFormFactory = createManagerFactory(transactionRulesForm);
